import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { canAddAttachment } from '@/lib/attachments';
import { PhotoPickCancelledError } from '@/lib/photo-errors';
import { pickShrunkPhoto } from '@/lib/pick-photo';

// One picker for every surface that takes image attachments (design.md,
// "Rich content"): pick -> upload to POST /me/photos -> hold the blob ids
// until the surface's own create call references them.
export function useAttachmentPicker() {
  const [photoIds, setPhotoIds] = useState<string[]>([]);

  const add = useMutation({
    mutationFn: async () => {
      const photo = await pickShrunkPhoto();

      return api.uploadPhoto(photo.base64, photo.mimeType);
    },
    onSuccess: (uploaded) => setPhotoIds((ids) => [...ids, uploaded.id]),
  });

  return {
    photoIds,
    canAdd: canAddAttachment(photoIds.length) && !add.isPending,
    isUploading: add.isPending,
    // A cancelled pick is the user's own choice, not an error to show.
    error: add.error instanceof PhotoPickCancelledError ? null : add.error,
    add: () => add.mutate(),
    // Best-effort blob cleanup: the blob counts against the upload cap
    // whether or not anything references it.
    remove: (id: string) => {
      setPhotoIds((ids) => ids.filter((existing) => existing !== id));
      api.deletePhoto(id).catch(() => {});
    },
    // After a successful send the blobs are referenced -- forget them
    // without deleting.
    reset: () => setPhotoIds([]),
  };
}
